"use client";

import { useEffect, useMemo, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useUserContext } from '../store/contexts/userContext/userContexts';
import { getSupabaseClient } from '../services/supabaseClient';
import { logout } from '../services/auth';

/**
 * ApprovalStatusGuard Component
 *
 * Prevents PENDING users and users without valid user records from accessing protected routes.
 * If a pending user or unregistered OAuth user tries to access /dashboard, they get signed out.
 *
 * Uses user context (already loaded) - NO extra API calls for approved users.
 */

// Routes that are allowed even for pending/unregistered users (for them to complete signup)
const ALLOWED_PENDING_ROUTES = [
  '/auth/sign-up',
  '/auth/sign-in',
  '/auth/verify-email',
  '/auth/mfa',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/callback',
  '/auth/reauthenticate',
  '/auth/pending-approval',
];

interface ApprovalStatusGuardProps {
  children?: React.ReactNode;
}

export function ApprovalStatusGuard({ children }: ApprovalStatusGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isInitializing, profileLoadFailed } = useUserContext();
  const hasRedirectedRef = useRef(false);

  // Get account status and user ID from user context
  // IMPORTANT: We need both accountStatus AND a valid user ID to confirm user exists in database
  const { accountStatus, hasValidUser } = useMemo(() => {
    if (isInitializing) {
      return { accountStatus: null, hasValidUser: false };
    }

    // Check if we have a real user with an ID from the backend
    // If user.user.id is missing or still the initial empty state, user doesn't exist in our database
    const userId = user?.user?.id;
    const status = user?.user?.accountStatus || null;

    // A user is valid if:
    // 1. They have a non-empty user ID, OR
    // 2. They have ANY known account status (active, trial_active, pending, etc.)
    //    Having a status means the backend recognizes this user
    // This prevents false redirects when user data is still loading
    const hasKnownStatus = status !== null && status !== undefined;
    const hasValidUser = !!(userId && userId !== '' && userId !== 'initial') || hasKnownStatus;

    return {
      accountStatus: status,
      hasValidUser
    };
  }, [user?.user, isInitializing]);

  useEffect(() => {
    // Skip check for auth routes
    if (ALLOWED_PENDING_ROUTES.some(route => pathname?.startsWith(route))) {
      hasRedirectedRef.current = false; // Reset on auth routes
      return;
    }

    // Skip if still initializing
    if (isInitializing) {
      return;
    }

    // We could not READ the profile — so we do not know whether this user has a
    // record, and must not act as if they don't.
    //
    // This block signs the user out and sends them to /auth/sign-up. It fires on
    // every authenticated route, and the empty user state that a failed fetch
    // leaves behind is indistinguishable from a genuinely unregistered OAuth
    // user. During the 2026-08-28 pooler outage that turned ~2 minutes of
    // unreachable auth into a hard eviction, ahead of the provider's own 12s
    // retry. Wait for that retry instead; if the backend really does say the
    // user has no record, profileLoadFailed is false and we fall through.
    if (profileLoadFailed) {
      return;
    }

    // Skip if already redirected
    if (hasRedirectedRef.current) {
      return;
    }

    // CRITICAL: Check if user has a valid record in our database
    // OAuth users who haven't completed sign-up will have a Supabase session but no user record
    if (!hasValidUser) {
      hasRedirectedRef.current = true;

      // Sign out and redirect to sign-up
      const signOutUnregisteredUser = async () => {
        try {
          await logout();
          const supabase = await getSupabaseClient();
          await supabase.auth.signOut();
        } catch (error) {
          console.error('Error signing out unregistered user:', error);
        }
        // Redirect to sign-up with oauth flag
        window.location.href = '/auth/sign-up?oauth=true&reason=incomplete_registration';
      };

      signOutUnregisteredUser();
      return;
    }

    // Check for pending accounts
    // NOTE: Do NOT sign out pending users - just redirect them to the pending approval page
    // They should stay logged in so they can see their status and refresh when approved
    if (accountStatus === 'pending') {
      hasRedirectedRef.current = true;

      // Just redirect to pending approval page without signing out
      // This allows the user to stay logged in and check their approval status
      window.location.href = '/auth/pending-approval';
      return;
    }

    // Active/approved users with valid records - do nothing, let them through
  }, [pathname, accountStatus, hasValidUser, isInitializing, profileLoadFailed]);

  // Always render children immediately
  return children ? <>{children}</> : null;
}

export default ApprovalStatusGuard;
